/**
 * Compass tape: a pre-rendered 720° SVG strip that is only translated per frame.
 */
import { fromHtml, h, setClass, TextSlot, TransformSlot } from './dom';
import { cardinal, normDeg, shortestDelta } from './format';

/** Pixels per degree on the tape. */
export const PPD = 3.2;
const SPAN_FROM = -180;
const SPAN_TO = 540;
const HEIGHT = 52;

const LABELS: Record<number, string> = {
  0: 'N',
  45: 'NO',
  90: 'O',
  135: 'SO',
  180: 'S',
  225: 'SW',
  270: 'W',
  315: 'NW',
};

function tapeSvg(): string {
  const width = (SPAN_TO - SPAN_FROM) * PPD;
  let ticks = '';
  let texts = '';
  for (let deg = SPAN_FROM; deg <= SPAN_TO; deg += 5) {
    const x = ((deg - SPAN_FROM) * PPD).toFixed(1);
    const n = normDeg(deg);
    const isCardinal = n % 90 === 0;
    const isInter = n % 45 === 0 && !isCardinal;
    const len = isCardinal ? 15 : isInter ? 12 : n % 10 === 0 ? 9 : 5;
    ticks += `M${x} ${HEIGHT}V${HEIGHT - len}`;
    if (isCardinal) {
      const fill = n === 0 ? '#ff4b52' : '#fff';
      texts += `<text x="${x}" y="26" font-size="20" font-weight="900" fill="${fill}">${LABELS[n]}</text>`;
    } else if (isInter) {
      texts += `<text x="${x}" y="25" font-size="14" font-weight="800" fill="#e6eef2">${LABELS[n]}</text>`;
    } else if (n % 30 === 0) {
      texts += `<text x="${x}" y="24" font-size="13" font-weight="700" fill="#b6c7d1">${n}</text>`;
    }
  }
  return `<svg width="${width}" height="${HEIGHT}" viewBox="0 0 ${width} ${HEIGHT}" aria-hidden="true" focusable="false">
<path d="${ticks}" stroke="#fff" stroke-width="2" stroke-opacity=".85"/>
<g font-family="Roboto, system-ui, sans-serif" text-anchor="middle">${texts}</g></svg>`;
}

export class CompassTape {
  readonly el: HTMLElement;
  private readonly strip: TransformSlot;
  private readonly limitMark: TransformSlot;
  private readonly shoreMark: TransformSlot;
  private readonly limitEl: HTMLElement;
  private readonly shoreEl: HTMLElement;
  private readonly readout: TextSlot;
  private hasHeading: boolean | null = null;
  private lastReadout = '';

  constructor() {
    const stripEl = h('div', 'uz-compass-strip');
    stripEl.append(fromHtml(tapeSvg()));
    this.limitEl = h('div', 'uz-compass-mark uz-compass-mark--limit');
    this.shoreEl = h('div', 'uz-compass-mark uz-compass-mark--shore');
    const readoutEl = h('div', 'uz-compass-readout', { 'aria-hidden': 'true' }, ['–']);
    this.el = h('div', 'uz-compass', { role: 'img', 'aria-label': 'Kompass' }, [
      h('div', 'uz-compass-bg'),
      h('div', 'uz-compass-tape', null, [stripEl, this.limitEl, this.shoreEl, h('div', 'uz-compass-center')]),
      readoutEl,
    ]);
    this.strip = new TransformSlot(stripEl, (v) => `translate3d(${v.toFixed(1)}px,0,0)`, 0.1);
    this.limitMark = new TransformSlot(this.limitEl, (v) => `translate3d(${v.toFixed(1)}px,0,0)`, 0.2);
    this.shoreMark = new TransformSlot(this.shoreEl, (v) => `translate3d(${v.toFixed(1)}px,0,0)`, 0.2);
    this.readout = new TextSlot(readoutEl);
  }

  /** Per frame: transforms only. */
  setHeading(heading: number | null, limitBearing: number | null, shoreBearing: number | null): void {
    const has = heading != null;
    if (has !== this.hasHeading) {
      this.hasHeading = has;
      setClass(this.el, 'is-nohead', !has);
    }
    const hd = has ? normDeg(heading) : 0;
    this.strip.set(-(hd - SPAN_FROM) * PPD);
    this.placeMark(this.limitEl, this.limitMark, has ? limitBearing : null, hd);
    this.placeMark(this.shoreEl, this.shoreMark, has ? shoreBearing : null, hd);
  }

  private placeMark(el: HTMLElement, slot: TransformSlot, bearing: number | null, heading: number): void {
    const off = bearing == null;
    if (el.hidden !== off) el.hidden = off;
    if (!off) slot.set(shortestDelta(heading, bearing) * PPD);
  }

  /** Throttled: numeric readout. */
  setReadout(heading: number | null): void {
    let text = '–';
    if (heading != null) {
      const d = Math.round(normDeg(heading)) % 360;
      text = `${d}° ${cardinal(d)}`;
    }
    if (text === this.lastReadout) return;
    this.lastReadout = text;
    this.readout.set(text);
    this.el.setAttribute('aria-label', heading == null ? 'Kompass: keine Richtung' : `Kompass: ${text}`);
  }
}
